import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { getBitacoraDefaultTitle } from 'src/utils/financeI18n';
import { getMomentLocale } from 'src/utils/appLocale';

const BitacoraListItem = ({ bitacora, isExpanded, onView, onEdit, onSave, onNotify, onDelete, canNotify, children }) => {
  const { t, i18n } = useTranslation('finance');
  const momentLocale = getMomentLocale(i18n.language);
  const { flowDate, title, status } = bitacora;
  const isDraft = status === 'draft';
  const displayTitle =
    title ||
    (flowDate
      ? moment(flowDate).locale(momentLocale).format(momentLocale === 'en' ? 'MMMM D, YYYY' : 'D [de] MMMM [de] YYYY')
      : t('bitacora.noTitle'));

  const handleHeaderClick = (e) => {
    if (onView) onView(bitacora);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    if (onEdit && isDraft) onEdit(bitacora);
  };

  const handleSave = (e) => {
    e.stopPropagation();
    if (onSave && isDraft) onSave(bitacora);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (onDelete && isDraft) onDelete(bitacora);
  };

  return (
    <div className="bitacora-card">
      <div
        className="bitacora-card-header"
        onClick={handleHeaderClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleHeaderClick(e)}
      >
        <div className="bitacora-card-header-row">
          <div className="bitacora-card-header-left">
            <div className="bitacora-card-header-text">
              <div className="bitacora-card-title">{displayTitle}</div>
              {!isDraft && bitacora?.deliveredAt && (
                <div className="bitacora-card-delivery-pie">
                  {t('bitacora.deliveredOn', {
                    date: moment(bitacora.deliveredAt).format('DD/MM/YYYY HH:mm'),
                    by: bitacora?.deliveredByEmail || bitacora?.deliveredBy || '-',
                  })}
                </div>
              )}
            </div>
          </div>
          <div className="bitacora-card-header-right">
            {isDraft && (
              <>
                <button
                  type="button"
                  className={`bitacora-card-btn ${isExpanded ? 'bitacora-card-btn--save' : 'bitacora-card-btn--edit'}`}
                  onClick={isExpanded ? handleSave : handleEdit}
                >
                  {isExpanded ? t('bitacora.save') : t('bitacora.edit')}
                </button>
                <button
                  type="button"
                  className="bitacora-card-btn bitacora-card-btn--delete"
                  onClick={handleDelete}
                >
                  {t('bitacora.delete')}
                </button>
              </>
            )}
            <span className={`bitacora-card-status bitacora-card-status--${isDraft ? 'draft' : 'delivered'}`}>
              {isDraft ? (
                <>
                  <svg className="bitacora-status-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  {t('bitacora.draft')}
                </>
              ) : (
                <>
                  <svg className="bitacora-status-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {t('bitacora.delivered')}
                </>
              )}
            </span>
            <div className={`bitacora-card-chevron ${isExpanded ? 'expanded' : ''}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className={`bitacora-card-content ${isExpanded ? 'expanded' : ''}`}>
        {children}
      </div>
    </div>
  );
};

export default BitacoraListItem;
